import { Fragment, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Crown, Download, Loader2, Lightbulb, ArrowRightLeft, AlertTriangle, Info,
  TrendingUp, Wallet, Users2, BarChart3, Trophy, PieChart, GitCompare,
  ExternalLink, Layers,
} from "lucide-react";
import { api, idr, fmtNum } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDataQuery } from "@/hooks/useDataQuery";
import LoadingState from "@/components/ui/loading-state";
import { MonthPicker, DatePicker } from "@/components/ui/date-picker";
import { bulanIniISO, formatBulan, formatTanggal } from "@/lib/tanggal";
import {
  PageHeader, TidakPunyaAkses, adaField, CHANNELS, CHANNEL_LABEL,
  LEAD_STAGES, LEAD_STAGE_LABEL, PLATFORM_LABEL,
} from "./_shared";
import { GradeBadge } from "./KolScoreboard";

/**
 * Dashboard Eksekutif Marketing (Fase 3, brief MKT-04) — satu layar yang
 * dipakai bos untuk memutuskan belanja iklan. Backend
 * (`routers/marketing/executive.py`) SUDAH menghitung semua rumus dan
 * SENGAJA menandai banyak sel `data_cukup: false` alih-alih menebak
 * atribusi lintas kanal — kontrak tampilan di sini:
 *
 * 1. **Tidak pernah menyamakan "data belum cukup" dengan 0.** Setiap sel yang
 *    backend tandai `data_cukup: false` (atau field-nya sama sekali tidak
 *    ada di objek, mis. baris kanal `produksi_konten`/`lainnya` yang memang
 *    tidak melacak omzet) dirender lewat <CatatanBadge> — bukan "-", bukan
 *    "0", bukan sel kosong — dengan alasan dari backend sebagai tooltip.
 * 2. **Klaim vs Nyata Iklan membandingkan JUMLAH, bukan Rupiah** (lihat
 *    KEPUTUSAN #3 di backend) — kolom klaim/nyata/gap TIDAK PERNAH diberi
 *    label mata uang. Kolom "Omzet Nyata Minimal" itu Rupiah, tapi berasal
 *    dari POS kita sendiri (bukan klaim platform), makanya dipisah kolom &
 *    diberi keterangan "batas bawah".
 * 3. **Kohort selalu menampilkan "Tanpa Sumber Info" apa adanya**, plus
 *    persentase cakupan datanya — tidak pernah disembunyikan.
 * 4. **Kerahasiaan margin double-gate**: kolom ROI/Margin hanya tampil kalau
 *    `can("marketing_margin","view")` DAN field `margin`/`roi` benar-benar
 *    ada di respons (`adaField`). Sengaja tidak cukup mengandalkan `adaField`
 *    saja — backend `strip_margin()` memakai whitelist `MARGIN_FIELDS` yang
 *    TIDAK memasukkan penanda `margin_tidak_lengkap`, jadi field itu bisa
 *    lolos ke user tanpa izin margin; gerbang `can()` di sini adalah
 *    pertahanan tambahan supaya UI tidak pernah membocorkan keberadaan
 *    tracking margin untuk user yang tidak berhak melihatnya.
 */
export default function MarketingExecutive() {
  const { can } = useAuth();
  const bolehLihat = can("marketing_executive", "view");
  const bolehMargin = can("marketing_margin", "view");

  const [period, setPeriod] = useState(bulanIniISO());
  const [tab, setTab] = useState("ringkasan"); // ringkasan | kohort | klaim
  const [sampaiKohort, setSampaiKohort] = useState("");
  const [exportingPdf, setExportingPdf] = useState(false);

  const { data, loading, error, refetch } = useDataQuery(
    ["marketing", "executive-summary", period],
    () => api.get("/marketing/executive", { params: { period } }).then((r) => r.data),
    { enabled: bolehLihat },
  );

  const { data: kohort, loading: loadingKohort, error: errorKohort, refetch: refetchKohort } = useDataQuery(
    ["marketing", "executive-kohort", sampaiKohort],
    () => api.get("/marketing/executive/kohort-source-info", {
      params: sampaiKohort ? { sampai: sampaiKohort } : {},
    }).then((r) => r.data),
    { enabled: bolehLihat && tab === "kohort" },
  );

  const { data: klaim, loading: loadingKlaim, error: errorKlaim, refetch: refetchKlaim } = useDataQuery(
    ["marketing", "executive-klaim-vs-nyata", period],
    () => api.get("/marketing/executive/klaim-vs-nyata-ads", { params: { period } }).then((r) => r.data),
    { enabled: bolehLihat && tab === "klaim" },
  );

  // Kelompokkan matrix ROAS/ROI per brand (baris brand_total + baris kanal di
  // bawahnya). Bergantung pada objek `data` (referensi stabil dari React
  // Query — hanya berubah saat data baru benar-benar datang), BUKAN pada
  // `data?.brand_channel_matrix || []` yang melahirkan array literal baru
  // SETIAP render kalau field itu belum ada — itu jebakan exhaustive-deps
  // yang pernah menjatuhkan halaman agent lain begitu dipasang ke rute.
  const grouped = useMemo(() => {
    const rows = data?.brand_channel_matrix || [];
    const map = {};
    rows.forEach((r) => {
      const key = r.brand || "(tanpa brand)";
      if (!map[key]) map[key] = { brand: key, total: null, channels: [] };
      if (r.level === "brand_total") map[key].total = r;
      else map[key].channels.push(r);
    });
    return Object.values(map)
      .sort((a, b) => a.brand.localeCompare(b.brand))
      .map((g) => ({
        ...g,
        channels: [...g.channels].sort((a, b) => CHANNELS.indexOf(a.channel) - CHANNELS.indexOf(b.channel)),
      }));
  }, [data]);

  const marginAda = bolehMargin
    && (data?.brand_channel_matrix || []).some((r) => adaField(r, "margin") || r.margin_tidak_lengkap === true);

  const unduhPdf = async () => {
    if (exportingPdf) return;
    setExportingPdf(true);
    try {
      const res = await api.get("/marketing/executive/export-pdf", { params: { period }, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `dashboard-eksekutif-marketing-${period}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF dashboard eksekutif berhasil diunduh.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengunduh PDF dashboard eksekutif.");
    } finally {
      setExportingPdf(false);
    }
  };

  if (!bolehLihat) {
    return (
      <div className="p-4 md:p-8 space-y-5">
        <PageHeader icon={Crown} title="Dashboard Eksekutif Marketing" />
        <TidakPunyaAkses label="Dashboard Eksekutif Marketing" testid="mkt-exec-tanpa-akses" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="mkt-exec-dashboard">
      <PageHeader
        icon={Crown} title="Dashboard Eksekutif Marketing"
        desc="Satu layar untuk keputusan belanja iklan: ROAS/ROI per brand & kanal, burn rate anggaran, performa KOL, funnel lead, dan rekomendasi realokasi berbasis aturan."
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <MonthPicker value={period} onChange={setPeriod} data-testid="mkt-exec-periode" className="w-32" />
            <button onClick={unduhPdf} disabled={exportingPdf} data-testid="mkt-exec-unduh-pdf"
                    className="h-9 px-4 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700 inline-flex items-center gap-2 disabled:opacity-50">
              {exportingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {exportingPdf ? "Menyiapkan..." : "Unduh PDF"}
            </button>
          </div>
        }
      />

      <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 gap-1">
        <button onClick={() => setTab("ringkasan")} data-testid="mkt-exec-tab-ringkasan"
                className={`px-3 h-8 rounded-md text-xs font-medium inline-flex items-center gap-1.5 ${tab === "ringkasan" ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
          <BarChart3 className="w-3.5 h-3.5" /> Ringkasan
        </button>
        <button onClick={() => setTab("kohort")} data-testid="mkt-exec-tab-kohort"
                className={`px-3 h-8 rounded-md text-xs font-medium inline-flex items-center gap-1.5 ${tab === "kohort" ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
          <PieChart className="w-3.5 h-3.5" /> Kohort Pembelian Ulang
        </button>
        <button onClick={() => setTab("klaim")} data-testid="mkt-exec-tab-klaim"
                className={`px-3 h-8 rounded-md text-xs font-medium inline-flex items-center gap-1.5 ${tab === "klaim" ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
          <GitCompare className="w-3.5 h-3.5" /> Klaim vs Nyata Iklan
        </button>
      </div>

      {/* ============ TAB: Ringkasan ============ */}
      {tab === "ringkasan" && (
        <LoadingState loading={loading} error={error} onRetry={refetch} varian="kartu" jumlahKartu={4} data-testid="mkt-exec-loading">
          {data && (
            <div className="space-y-5" data-testid="mkt-exec-tab-ringkasan-panel">
              {data.jumlah_baris_data_belum_cukup > 0 && (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 flex items-start gap-2.5 text-sm text-slate-600" data-testid="mkt-exec-banner-data-kurang">
                  <Info className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                  <p>
                    {data.jumlah_baris_data_belum_cukup} baris pada laporan ini ditandai <b>&ldquo;Data belum cukup&rdquo;</b> —
                    bukan nol, lihat penjelasan pada badge/tooltip masing-masing baris.
                  </p>
                </div>
              )}

              {/* Insight */}
              <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="mkt-exec-insight">
                <p className="text-sm font-semibold text-slate-900 mb-2 inline-flex items-center gap-1.5">
                  <Lightbulb className="w-4 h-4 text-emerald-600" /> Insight (Berbasis Aturan)
                </p>
                <ul className="space-y-1.5">
                  {(data.insight || []).map((s, i) => (
                    <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" /> {s}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Rekomendasi realokasi */}
              {(data.rekomendasi_realokasi || []).length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="mkt-exec-rekomendasi">
                  <p className="text-sm font-semibold text-slate-900 mb-3 inline-flex items-center gap-1.5">
                    <ArrowRightLeft className="w-4 h-4 text-emerald-600" /> Rekomendasi Realokasi Anggaran
                  </p>
                  <div className="space-y-2">
                    {data.rekomendasi_realokasi.map((r, i) => (
                      <div key={i} className="border border-slate-200 rounded-lg p-3 bg-slate-50/60 flex items-start gap-2.5" data-testid={`mkt-exec-rekom-${i}`}>
                        <ArrowRightLeft className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm text-slate-700">{r.alasan}</p>
                          {r.estimasi_nominal_digeser != null && (
                            <p className="text-xs font-mono text-emerald-700 mt-1">Estimasi nominal digeser: {idr(r.estimasi_nominal_digeser)}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-400 mt-3">
                    Rekomendasi murni aturan ambang (bukan AI) — eksekusi tetap butuh approval CEO lewat menu Budget/Beban Marketing.
                  </p>
                </div>
              )}

              {/* Matrix ROAS/ROI per brand & kanal */}
              <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="mkt-exec-matrix">
                <p className="text-sm font-semibold text-slate-900 mb-3 inline-flex items-center gap-1.5">
                  <Layers className="w-4 h-4 text-emerald-600" /> ROAS/ROI per Brand &amp; Kanal
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr className="text-left">
                        <th className="px-3 py-2.5 font-semibold">Brand / Kanal</th>
                        <th className="px-3 py-2.5 font-semibold text-right">Spend</th>
                        <th className="px-3 py-2.5 font-semibold text-right">Omzet</th>
                        <th className="px-3 py-2.5 font-semibold text-right">ROAS</th>
                        {marginAda && <th className="px-3 py-2.5 font-semibold text-right">ROI</th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {grouped.length === 0 ? (
                        <tr><td colSpan={marginAda ? 5 : 4} className="px-3 py-8 text-center text-slate-400">Belum ada spend atau omzet teratribusi pada periode ini.</td></tr>
                      ) : grouped.map((g) => (
                        <Fragment key={g.brand}>
                          {g.total && (
                            <tr className="bg-slate-50/70" data-testid={`mkt-exec-matrix-brand-${slug(g.brand)}`}>
                              <td className="px-3 py-2.5 font-semibold text-slate-900">
                                {g.brand} <span className="text-[10px] font-normal text-slate-400">(total brand)</span>
                              </td>
                              <td className="px-3 py-2.5 text-right font-mono">{idr(g.total.spend)}</td>
                              <td className="px-3 py-2.5 text-right font-mono">{renderOmzetCell(g.total)}</td>
                              <td className="px-3 py-2.5 text-right font-mono">{renderRoasCell(g.total)}</td>
                              {marginAda && <td className="px-3 py-2.5 text-right font-mono">{renderRoiCell(g.total)}</td>}
                            </tr>
                          )}
                          {g.channels.map((c) => (
                            <tr key={c.channel} data-testid={`mkt-exec-matrix-channel-${slug(g.brand)}-${c.channel}`}>
                              <td className="px-3 py-2.5 pl-7 text-slate-600">{CHANNEL_LABEL[c.channel] || c.channel}</td>
                              <td className="px-3 py-2.5 text-right font-mono">{idr(c.spend)}</td>
                              <td className="px-3 py-2.5 text-right font-mono">{renderOmzetCell(c)}</td>
                              <td className="px-3 py-2.5 text-right font-mono">{renderRoasCell(c)}</td>
                              {marginAda && <td className="px-3 py-2.5 text-right font-mono">{renderRoiCell(c)}</td>}
                            </tr>
                          ))}
                        </Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
                {data.catatan_brand_channel_matrix && (
                  <p className="text-[11px] text-slate-400 mt-3">{data.catatan_brand_channel_matrix}</p>
                )}
              </div>

              {/* Burn rate anggaran */}
              <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 space-y-3" data-testid="mkt-exec-burn">
                <p className="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5">
                  <Wallet className="w-4 h-4 text-emerald-600" /> Burn Rate Anggaran — {formatBulan(period)}
                </p>
                {(data.budget_burn || []).length === 0 ? (
                  <p className="text-sm text-slate-400 py-4 text-center">Belum ada anggaran diajukan untuk periode ini.</p>
                ) : (
                  <div className="space-y-3">
                    {data.budget_burn.map((b) => (
                      <div key={b.brand} className="border border-slate-100 rounded-lg p-3" data-testid={`mkt-exec-burn-${slug(b.brand)}`}>
                        <div className="flex items-center justify-between mb-1.5 flex-wrap gap-1">
                          <p className="text-sm font-medium text-slate-800">{b.brand}</p>
                          {!b.ada_budget ? (
                            <span className="text-xs text-slate-400">Belum ada anggaran disetujui — realisasi {idr(b.grand_total)}</span>
                          ) : (
                            <p className={`text-sm font-mono font-bold ${ALERT_STYLE[b.total_alert]?.text || "text-slate-700"}`}>
                              {idr(b.grand_total)} / {idr(b.amount_total)}{b.total_pct_used != null && ` (${b.total_pct_used}%)`}
                            </p>
                          )}
                        </div>
                        {b.ada_budget && (
                          <>
                            <div className="h-2 rounded-full bg-slate-100 overflow-hidden mb-2">
                              <div className={`h-full ${ALERT_STYLE[b.total_alert]?.bar || "bg-slate-400"}`} style={{ width: `${Math.min(100, b.total_pct_used || 0)}%` }} />
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                              {(b.per_bucket || []).map((pb) => {
                                const st = ALERT_STYLE[pb.alert] || ALERT_STYLE.hijau;
                                return (
                                  <div key={pb.bucket} className={`rounded border p-2 ${pb.alokasi > 0 ? st.card : "border-slate-100"}`}>
                                    <p className="text-[10px] text-slate-500">{BUCKET_LABEL[pb.bucket] || pb.bucket}</p>
                                    <p className={`text-xs font-mono font-semibold ${pb.alokasi > 0 ? st.text : "text-slate-600"}`}>{idr(pb.actual)}</p>
                                    {pb.pct_used != null && <p className={`text-[10px] ${st.text}`}>{pb.pct_used}%</p>}
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {(data.brands_tanpa_budget_periode_ini || []).length > 0 && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5" data-testid="mkt-exec-brand-tanpa-anggaran">
                    Brand dengan spend tercatat tapi belum mengajukan anggaran periode ini: {data.brands_tanpa_budget_periode_ini.join(", ")}.
                  </p>
                )}
              </div>

              {/* Performa KOL */}
              <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="mkt-exec-kol">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                  <p className="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5">
                    <Trophy className="w-4 h-4 text-emerald-600" /> Performa KOL
                  </p>
                  <Link to="/marketing/kol-score" className="text-xs text-emerald-700 hover:underline inline-flex items-center gap-1">
                    Lihat papan peringkat lengkap <ExternalLink className="w-3 h-3" />
                  </Link>
                </div>
                <p className="text-xs text-slate-500 mb-3">
                  {data.kol_performance.jumlah_dinilai} KOL punya grade resmi periode ini
                  {data.kol_performance.jumlah_belum_cukup_data > 0 && `, ${data.kol_performance.jumlah_belum_cukup_data} lainnya data belum cukup`}.
                  {data.kol_performance.median_cpv_populasi != null && (
                    <> Median CPV populasi: <span className="font-mono">{idr(data.kol_performance.median_cpv_populasi)}/view</span>.</>
                  )}
                </p>
                {data.kol_performance.catatan && (
                  <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-2.5 mb-3">{data.kol_performance.catatan}</p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <KolList title="Teratas" items={data.kol_performance.top} testid="mkt-exec-kol-top" />
                  <KolList title="Terbawah" items={data.kol_performance.bottom} testid="mkt-exec-kol-bottom" />
                </div>
                {(data.kol_performance.watchlist_disarankan || []).length > 0 && (
                  <div className="mt-4 space-y-2">
                    {data.kol_performance.watchlist_disarankan.map((w) => (
                      <div key={w.kol_id} className="bg-rose-50 border border-rose-200 rounded-lg p-3 flex items-start gap-2 text-sm text-rose-800" data-testid={`mkt-exec-watchlist-${slug(w.kol_id)}`}>
                        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> {w.pesan}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Corong lead */}
              <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="mkt-exec-funnel">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <p className="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5">
                    <Users2 className="w-4 h-4 text-emerald-600" /> Corong Lead (snapshot saat ini)
                  </p>
                  {data.lead_funnel.stale_count > 0 && (
                    <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">
                      <AlertTriangle className="w-3 h-3" /> {data.lead_funnel.stale_count} lead mengendap &gt;48 jam
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  {LEAD_STAGES.map((stage) => (
                    <FunnelBox key={stage} stage={stage} value={data.lead_funnel.per_stage?.[stage] || 0}
                               max={Math.max(1, ...LEAD_STAGES.map((s) => data.lead_funnel.per_stage?.[s] || 0))} />
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-3">
                  Total lead: <b>{fmtNum(data.lead_funnel.total)}</b> · Closed won: <b>{fmtNum(data.lead_funnel.closed_won)}</b>
                  {data.lead_funnel.conversion_rate_pct != null && <> · Conversion rate: <b>{data.lead_funnel.conversion_rate_pct}%</b></>}
                </p>
              </div>

              {/* Omzet per sumber info */}
              <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="mkt-exec-source-info">
                <div className="p-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
                  <p className="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5">
                    <TrendingUp className="w-4 h-4 text-emerald-600" /> Kontribusi Omzet per Sumber Info
                  </p>
                  {data.omzet_per_source_info.pct_transaksi_terisi_sumber_info != null && (
                    <span className="text-xs text-slate-500">
                      Cakupan pengisian: <b>{data.omzet_per_source_info.pct_transaksi_terisi_sumber_info}%</b> dari transaksi periode ini
                    </span>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr className="text-left">
                        <th className="px-3 py-2.5 font-semibold">Sumber Info</th>
                        <th className="px-3 py-2.5 font-semibold text-right">Transaksi</th>
                        <th className="px-3 py-2.5 font-semibold text-right">Omzet</th>
                        <th className="px-3 py-2.5 font-semibold text-right">Kontribusi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {(data.omzet_per_source_info.per_bucket || []).length === 0 ? (
                        <tr><td colSpan={4} className="px-3 py-8 text-center text-slate-400">Belum ada transaksi pada periode ini.</td></tr>
                      ) : data.omzet_per_source_info.per_bucket.map((b) => (
                        <tr key={b.source_info || "_kosong"} className={!b.source_info ? "bg-amber-50/40" : ""} data-testid={`mkt-exec-sourceinfo-${slug(b.source_info || "tanpa")}`}>
                          <td className="px-3 py-2.5">
                            {b.label}
                            {!b.source_info && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 align-middle">data lama</span>}
                          </td>
                          <td className="px-3 py-2.5 text-right font-mono">{fmtNum(b.jumlah_transaksi)}</td>
                          <td className="px-3 py-2.5 text-right font-mono">{idr(b.omzet)}</td>
                          <td className="px-3 py-2.5 text-right font-mono">{b.kontribusi_omzet_pct != null ? `${b.kontribusi_omzet_pct}%` : "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </LoadingState>
      )}

      {/* ============ TAB: Kohort Pembelian Ulang ============ */}
      {tab === "kohort" && (
        <div className="space-y-5" data-testid="mkt-exec-tab-kohort-panel">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-slate-500">Hitung sampai tanggal</span>
            <DatePicker value={sampaiKohort} onChange={setSampaiKohort} placeholder="Hari ini" data-testid="mkt-exec-kohort-sampai" className="w-40" />
          </div>
          <LoadingState loading={loadingKohort} error={errorKohort} onRetry={refetchKohort} varian="kartu" jumlahKartu={4} data-testid="mkt-exec-kohort-loading">
            {kohort && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <KpiMini label="Total Pelanggan Unik (ber-HP)" value={fmtNum(kohort.total_pelanggan_unik_dgn_hp)} />
                  <KpiMini label="Transaksi Dihitung (ber-HP)" value={fmtNum(kohort.total_transaksi_dgn_hp_dihitung)} />
                  <KpiMini label="Cakupan Sumber Info (Pelanggan)" value={kohort.cakupan_sumber_info_pelanggan_pct != null ? `${kohort.cakupan_sumber_info_pelanggan_pct}%` : "-"} />
                  <KpiMini label="Cakupan Sumber Info (Transaksi)" value={kohort.cakupan_sumber_info_transaksi_berhp_pct != null ? `${kohort.cakupan_sumber_info_transaksi_berhp_pct}%` : "-"} />
                </div>

                <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="mkt-exec-kohort-table">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-slate-600">
                        <tr className="text-left">
                          <th className="px-3 py-2.5 font-semibold">Sumber Info (Transaksi Pertama)</th>
                          <th className="px-3 py-2.5 font-semibold text-right">Jumlah Pelanggan</th>
                          <th className="px-3 py-2.5 font-semibold text-right">Repeat Rate</th>
                          <th className="px-3 py-2.5 font-semibold text-right">Total Omzet (All-Time)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {(kohort.per_bucket || []).length === 0 ? (
                          <tr><td colSpan={4} className="px-3 py-8 text-center text-slate-400">Belum ada pelanggan dengan nomor HP tercatat.</td></tr>
                        ) : kohort.per_bucket.map((b) => (
                          <tr key={b.source_info || "_kosong"} className={!b.source_info ? "bg-amber-50/40" : ""} data-testid={`mkt-exec-kohort-row-${slug(b.source_info || "tanpa")}`}>
                            <td className="px-3 py-2.5">
                              {b.label}
                              {!b.source_info && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 align-middle">ditampilkan terbuka</span>}
                            </td>
                            <td className="px-3 py-2.5 text-right font-mono">{fmtNum(b.jumlah_pelanggan)}</td>
                            <td className="px-3 py-2.5 text-right font-mono">{b.repeat_rate_pct != null ? `${b.repeat_rate_pct}%` : "-"}</td>
                            <td className="px-3 py-2.5 text-right font-mono">{idr(b.total_omzet_all_time)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <p className="text-xs text-slate-400">
                  Menghitung dari seluruh histori transaksi hingga {formatTanggal(kohort.sampai)}.{kohort.catatan ? ` ${kohort.catatan}` : ""}
                </p>
              </>
            )}
          </LoadingState>
        </div>
      )}

      {/* ============ TAB: Klaim vs Nyata Iklan ============ */}
      {tab === "klaim" && (
        <div className="space-y-5" data-testid="mkt-exec-tab-klaim-panel">
          <LoadingState loading={loadingKlaim} error={errorKlaim} onRetry={refetchKlaim} varian="kartu" jumlahKartu={2} data-testid="mkt-exec-klaim-loading">
            {klaim && (
              <>
                {klaim.catatan_metodologi && (
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 flex items-start gap-2.5 text-sm text-slate-600" data-testid="mkt-exec-klaim-metodologi">
                    <Info className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" /> {klaim.catatan_metodologi}
                  </div>
                )}
                {(klaim.per_campaign || []).length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-10">Tidak ada log iklan (ads) pada periode ini.</p>
                ) : (
                  <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="mkt-exec-klaim-table">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600">
                          <tr className="text-left">
                            <th className="px-3 py-2.5 font-semibold">Campaign</th>
                            <th className="px-3 py-2.5 font-semibold">Platform</th>
                            <th className="px-3 py-2.5 font-semibold text-right">Spend Iklan</th>
                            <th className="px-3 py-2.5 font-semibold text-right">Klaim Konversi (Platform)</th>
                            <th className="px-3 py-2.5 font-semibold text-right">Closing Nyata (POS)</th>
                            <th className="px-3 py-2.5 font-semibold text-right">Gap</th>
                            <th className="px-3 py-2.5 font-semibold text-right">Omzet Nyata Minimal</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {klaim.per_campaign.map((c) => (
                            <tr key={c.campaign_id} data-testid={`mkt-exec-klaim-row-${slug(c.campaign_id)}`}>
                              <td className="px-3 py-2.5">
                                <p className="font-mono text-[11px] text-slate-500">{c.code}</p>
                                <p className="text-slate-900 font-medium">{c.nama}</p>
                                <p className="text-[11px] text-slate-400">{c.brand}</p>
                              </td>
                              <td className="px-3 py-2.5 text-slate-600">{(c.platforms || []).map((p) => PLATFORM_LABEL[p] || p).join(", ") || "-"}</td>
                              <td className="px-3 py-2.5 text-right font-mono">{idr(c.spend)}</td>
                              <td className="px-3 py-2.5 text-right font-mono">{fmtNum(c.klaim_conversions_platform)}</td>
                              <td className="px-3 py-2.5 text-right font-mono">{fmtNum(c.nyata_closing_terverifikasi_pos)}</td>
                              <td className="px-3 py-2.5 text-right">
                                {c.data_cukup ? (
                                  <span className={`font-mono font-semibold ${c.indikasi_over_claim ? "text-rose-600" : "text-slate-700"}`}>
                                    {c.gap_pct > 0 ? "+" : ""}{c.gap_pct}%
                                  </span>
                                ) : (
                                  <CatatanBadge catatan={c.catatan} />
                                )}
                                {c.indikasi_over_claim && (
                                  <div className="mt-1">
                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 font-medium whitespace-nowrap">Indikasi over-claim</span>
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-2.5 text-right font-mono text-slate-500" title="Batas bawah — closing lewat lead tanpa kode promo tidak tercatat omzetnya di sini">
                                {idr(c.nyata_omzet_terverifikasi_pos_minimal)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </LoadingState>
        </div>
      )}
    </div>
  );
}

// ============ Duplikasi kecil disengaja (hanya label tampilan) — pola sama
// dengan budget.py/expense.py sendiri yang mengembar EXPENSE_CATEGORY_TO_BUCKET
// per berkas (lihat komentar di MarketingBudget.jsx). ============
const BUCKET_LABEL = {
  kol: "KOL / Endorsement", ads: "Iklan Digital (Ads)", pfp: "PFP / Paid Promote",
  production: "Produksi Konten", other: "Lainnya",
};
const ALERT_STYLE = {
  hijau: { bar: "bg-emerald-500", text: "text-emerald-700", card: "border-emerald-200 bg-emerald-50/50" },
  kuning: { bar: "bg-amber-500", text: "text-amber-700", card: "border-amber-300 bg-amber-50" },
  merah: { bar: "bg-rose-500", text: "text-rose-700", card: "border-rose-300 bg-rose-50" },
};

/** ID uji yang aman dari string bebas (nama brand/kol bisa berisi spasi/slash) — hanya untuk data-testid. */
function slug(s) {
  return String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-+|-+$)/g, "") || "x";
}

/** Badge "data belum cukup" — SATU bentuk untuk seluruh halaman ini, alasan
 *  dari backend ditaruh sebagai tooltip (`title`), bukan dibuang. Dipakai
 *  tiap kali sebuah sel TIDAK bisa dihitung — tidak pernah diganti "-"/"0". */
function CatatanBadge({ label = "Data belum cukup", catatan }) {
  return (
    <span title={catatan || undefined}
          className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-medium bg-slate-100 text-slate-500 whitespace-nowrap">
      <Info className="w-3 h-3" /> {label}
    </span>
  );
}

/** Kolom Omzet — field `omzet` TIDAK ADA sama sekali untuk baris kanal yang
 *  memang tidak melacak omzet per-campaign (produksi_konten/lainnya) atau
 *  campaign multi-kanal (KEPUTUSAN #1/#2 backend) — beda dari nilai 0. */
function renderOmzetCell(row) {
  if (!adaField(row, "omzet")) return <CatatanBadge label="Tidak dilacak" catatan={row.catatan} />;
  return idr(row.omzet);
}

/** Kolom ROAS — `data_cukup: false` berarti pembagi (spend) tidak valid untuk
 *  baris ini (bukan berarti ROAS-nya 0). */
function renderRoasCell(row) {
  if (!row.data_cukup) return <CatatanBadge catatan={row.catatan || "ROAS tidak bisa dihitung untuk baris ini pada periode ini."} />;
  if (row.roas == null) return <CatatanBadge catatan="ROAS tidak bisa dihitung." />;
  return `${row.roas.toFixed(2)}x`;
}

/** Kolom ROI — HANYA dipanggil kalau kolomnya sudah diputuskan tampil
 *  (`marginAda`, digerbangi `can("marketing_margin","view")` di komponen
 *  induk). `margin_tidak_lengkap` = sebagian redemption baris ini belum
 *  tercatat modal/margin-nya (KEPUTUSAN #6 backend) — ditandai, BUKAN
 *  dihitung ulang dari angka lain. */
function renderRoiCell(row) {
  if (!row.data_cukup) return <CatatanBadge catatan={row.catatan || "ROI tidak bisa dihitung untuk baris ini pada periode ini."} />;
  if (adaField(row, "roi")) {
    return row.roi != null ? `${(row.roi * 100).toFixed(1)}%` : <CatatanBadge catatan="ROI tidak bisa dihitung." />;
  }
  if (row.margin_tidak_lengkap) {
    return <CatatanBadge label="Margin belum lengkap" catatan="Sebagian transaksi pada baris ini belum tercatat modal/margin — ROI tidak bisa dihitung akurat." />;
  }
  return <CatatanBadge />;
}

/** Daftar KOL teratas/terbawah — kartu skor SENGAJA tidak pernah menampilkan
 *  angka untuk KOL berdata tipis, hanya <GradeBadge> "Data belum cukup"
 *  (dari KolScoreboard.jsx, dipakai bersama supaya konsisten satu sumber). */
function KolList({ title, items, testid }) {
  return (
    <div>
      <p className="text-xs font-semibold text-slate-600 mb-2">{title}</p>
      {(!items || items.length === 0) ? (
        <p className="text-xs text-slate-400 py-3">Tidak ada data.</p>
      ) : (
        <ul className="divide-y divide-slate-100 border border-slate-100 rounded-lg" data-testid={testid}>
          {items.map((r) => (
            <li key={r.kol_id} className="flex items-center justify-between gap-2 px-3 py-2" data-testid={`${testid}-${slug(r.kol_id)}`}>
              <Link to={`/marketing/kol-score/${r.kol_id}`} className="text-sm text-slate-800 hover:text-emerald-700 hover:underline font-medium truncate">
                {r.kol_nama || r.kol_id.slice(0, 8)}
              </Link>
              <div className="flex items-center gap-2 shrink-0">
                {r.score != null && <span className="text-xs font-mono text-slate-500">{r.score.toFixed(1)}</span>}
                <GradeBadge grade={r.grade} insufficientData={r.insufficient_data} reason={r.insufficient_data_reason} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function KpiMini({ label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
      <p className="text-[11px] text-slate-500">{label}</p>
      <p className="text-lg font-bold text-slate-900 mt-0.5 font-mono">{value}</p>
    </div>
  );
}

function FunnelBox({ stage, value, max }) {
  const pct = Math.max(4, Math.round((value / max) * 100));
  const warna = stage === "closed_won" ? "bg-emerald-500" : stage === "closed_lost" ? "bg-rose-400" : "bg-sky-400";
  return (
    <div className="border border-slate-100 rounded-lg p-2.5">
      <p className="text-[11px] text-slate-500">{LEAD_STAGE_LABEL[stage]}</p>
      <p className="text-lg font-bold text-slate-900 font-mono">{value}</p>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mt-1.5">
        <div className={`h-full rounded-full ${warna}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
